import React from 'react';

import { Anchor, Badge, Button, Group, Modal, Skeleton, Stack, Text, Title, Tooltip } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconExternalLink, IconPlus, IconRefresh, IconSearch, IconUnlink } from '@tabler/icons-react';
import InfoCard from 'components/InfoCard';
import { useAuth } from 'context/auth-context';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import useAthleteGLMStatus from 'hooks/glmLink/useAthleteGLMStatus';
import useCreateAthleteGLMContact from 'hooks/glmLink/useCreateAthleteGLMContact';
import useRetryAthleteGLMLink from 'hooks/glmLink/useRetryAthleteGLMLink';
import useUnlinkAthleteGLM from 'hooks/glmLink/useUnlinkAthleteGLM';
import { useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import { AthleteGLMLinkStatus } from 'types';
import notificationHandler from 'utils/notifications';

import GLMContactSearchModal from './GLMContactSearchModal';

dayjs.extend(utc);
dayjs.extend(timezone);

const LINK_STATE_BADGE: Record<AthleteGLMLinkStatus['linkState'], { label: string; color: string }> = {
  linked: { label: 'Linked', color: 'green' },
  unlinked: { label: 'Not linked', color: 'gray' },
  dependent: { label: 'Dependent', color: 'gray' },
  broken: { label: 'Broken', color: 'red' },
};

// Small wrapper so the value column right-aligns cleanly.
const ValueCell = ({ children }: { children: React.ReactNode }): JSX.Element => (
  <Text size='sm' align='right' style={{ maxWidth: 220, wordBreak: 'break-word' }} component='div'>
    {children}
  </Text>
);

const Row = ({ label, children }: { label: string; children: React.ReactNode }): JSX.Element => (
  <Group position='apart' px='md' noWrap align='flex-start'>
    <Text size='sm' color='dimmed'>
      {label}
    </Text>
    <ValueCell>{children}</ValueCell>
  </Group>
);

const GLMLinkCard = ({ athleteId }: { athleteId: string }): JSX.Element => {
  const { session } = useAuth();
  const { data, isLoading } = useAthleteGLMStatus(athleteId);
  const status = data?.data;

  const [searchOpened, { open: openSearch, close: closeSearch }] = useDisclosure(false);
  const [unlinkOpened, { open: openUnlink, close: closeUnlink }] = useDisclosure(false);

  const { successNotification, errorNotification } = notificationHandler();
  const {
    trackAthleteGLMUnlinked,
    trackAthleteGLMContactCreated,
    trackAthleteGLMLinked,
    trackAthleteGLMLinkRetried,
  } = useStaffTracking();

  const { mutate: unlink, isLoading: unlinking } = useUnlinkAthleteGLM();
  const { mutate: createContact, isLoading: creating } = useCreateAthleteGLMContact();
  const { mutate: retry, isLoading: retrying } = useRetryAthleteGLMLink();

  const handleCreate = (): void => {
    createContact(athleteId, {
      onSuccess: () => {
        trackAthleteGLMContactCreated({ athlete_id: athleteId });
        trackAthleteGLMLinked({ athlete_id: athleteId, source: 'create_contact' });
        successNotification('GLM contact created', 'A new GLM contact was created and linked.');
      },
      onError: error => errorNotification('Create contact failed', error.response?.data?.message),
    });
  };

  const handleRetry = (): void => {
    retry(athleteId, {
      onSuccess: () => {
        trackAthleteGLMLinkRetried({ athlete_id: athleteId });
        successNotification('Link retried', 'The link attempt was retried.');
      },
      onError: error => errorNotification('Retry failed', error.response?.data?.message),
    });
  };

  const handleUnlink = (): void => {
    unlink(athleteId, {
      onSuccess: () => {
        trackAthleteGLMUnlinked({ athlete_id: athleteId });
        successNotification('Unlinked', 'The athlete is no longer linked to a GLM contact.');
      },
      onError: error => errorNotification('Unlink failed', error.response?.data?.message),
      onSettled: () => closeUnlink(),
    });
  };

  const renderBody = (linkStatus: AthleteGLMLinkStatus): JSX.Element => {
    if (linkStatus.linkState === 'dependent') {
      return (
        <Text size='sm' color='dimmed' px='md'>
          Dependent athletes cannot be linked to GLM.
        </Text>
      );
    }

    if (linkStatus.linkState === 'linked') {
      const synced = linkStatus.lastSyncedAt
        ? dayjs(linkStatus.lastSyncedAt)
            .tz(session?.gym?.timezone ?? 'UTC')
            .format('MMM D, YYYY h:mm A')
        : 'Never';
      return (
        <Stack spacing='xs'>
          <Row label='Contact'>{linkStatus.contactName ?? linkStatus.contactId}</Row>
          <Row label='Email'>
            <Group spacing={4} position='right' noWrap>
              <Text size='sm'>{linkStatus.email ?? '-'}</Text>
              <Tooltip
                multiline
                width={220}
                label='Email is not synced to GLM after the contact is created.'
                withinPortal
              >
                <Text size='xs' color='dimmed' style={{ cursor: 'help' }}>
                  (read-only)
                </Text>
              </Tooltip>
            </Group>
          </Row>
          <Row label='Last synced'>{synced}</Row>
          <Group position='right' px='md' pt='xs' spacing='sm'>
            {linkStatus.viewUrl && (
              <Anchor href={linkStatus.viewUrl} target='_blank' rel='noopener noreferrer' size='sm'>
                <Group spacing={4} noWrap>
                  <IconExternalLink size={16} />
                  View in GLM
                </Group>
              </Anchor>
            )}
            <Button
              size='xs'
              variant='outline'
              color='red'
              leftIcon={<IconUnlink size={16} />}
              onClick={openUnlink}
            >
              Unlink
            </Button>
          </Group>
        </Stack>
      );
    }

    if (linkStatus.linkState === 'broken') {
      return (
        <Stack spacing='md' px='md'>
          <Text size='sm' color='dimmed'>
            {"We're having trouble connecting to this integration. Please try again later."}
          </Text>
          <Group position='right' spacing='sm'>
            <Button
              size='xs'
              variant='outline'
              leftIcon={<IconRefresh size={16} />}
              loading={retrying}
              onClick={handleRetry}
            >
              Retry
            </Button>
            <Button
              size='xs'
              variant='outline'
              color='red'
              leftIcon={<IconUnlink size={16} />}
              onClick={openUnlink}
            >
              Unlink
            </Button>
          </Group>
        </Stack>
      );
    }

    // unlinked, non-dependent
    return (
      <Group position='right' px='md' spacing='sm'>
        <Button size='xs' variant='outline' leftIcon={<IconSearch size={16} />} onClick={openSearch}>
          Link to existing contact
        </Button>
        <Button size='xs' leftIcon={<IconPlus size={16} />} loading={creating} onClick={handleCreate}>
          Create new GLM contact
        </Button>
      </Group>
    );
  };

  return (
    <>
      <InfoCard
        header={<Title order={4}>Gym Lead Machine</Title>}
        actionButton={
          status ? (
            <Badge color={LINK_STATE_BADGE[status.linkState].color} variant='filled' radius='md'>
              {LINK_STATE_BADGE[status.linkState].label}
            </Badge>
          ) : null
        }
        content={
          isLoading || !status ? (
            <Stack spacing='xs' px='md'>
              <Skeleton height={16} />
              <Skeleton height={16} width='60%' />
            </Stack>
          ) : (
            renderBody(status)
          )
        }
      />

      <GLMContactSearchModal opened={searchOpened} onClose={closeSearch} athleteId={athleteId} />

      <Modal
        centered
        opened={unlinkOpened}
        onClose={closeUnlink}
        title={<Title order={4}>Unlink from GLM</Title>}
        size='md'
      >
        <Stack>
          <Text size='sm'>Are you sure you want to unlink this athlete from their GLM contact?</Text>
          <Text size='sm' color='dimmed'>
            Unlinking removes the connection on both sides. If you have inbound lifecycle flows configured
            (e.g. a tag or pipeline-stage flow), a future matching GLM event may automatically re-link this
            athlete.
          </Text>
          <Group position='right'>
            <Button variant='default' onClick={closeUnlink}>
              Cancel
            </Button>
            <Button color='red' loading={unlinking} onClick={handleUnlink}>
              Unlink
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};

export default GLMLinkCard;
